import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { getPreferences, savePreferences, resetToDefaults } from '../services/dashboardPreferencesService';

const DashboardPreferencesContext = createContext(null);

export function DashboardPreferencesProvider({ children }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const saveTimer = useRef(null);

  /**
   * Debounced save.
   */
  const debouncedSave = useCallback((updatedCards) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      savePreferences(updatedCards);
    }, 800);
  }, []);

  /**
   * Initialize/load preferences.
   */
  const initializePreferences = useCallback(async () => {
    if (initialized) return cards;
    setLoading(true);
    try {
      const loaded = await getPreferences();
      setCards(loaded);
      setInitialized(true);
      return loaded;
    } finally {
      setLoading(false);
    }
  }, [initialized, cards]);

  /**
   * Reorder cards (from drag-and-drop).
   */
  const reorderCards = useCallback((newCards) => {
    const reordered = newCards.map((c, idx) => ({ ...c, order: idx }));
    setCards(reordered);
    debouncedSave(reordered);
  }, [debouncedSave]);

  /**
   * Toggle card visibility.
   */
  const toggleCardVisibility = useCallback((cardId) => {
    setCards(prev => {
      const card = prev.find(c => c.id === cardId);
      if (!card) return prev;

      const updated = prev.map(c => {
        if (c.id === cardId) {
          return { ...c, visible: !c.visible };
        }
        return c;
      });

      // If showing a hidden card, move it to end of visible cards
      if (!card.visible) {
        const visibleCards = updated.filter(c => c.visible && c.id !== cardId);
        const hiddenCards = updated.filter(c => !c.visible);
        const theCard = updated.find(c => c.id === cardId);
        const reordered = [...visibleCards, theCard, ...hiddenCards].map((c, idx) => ({ ...c, order: idx }));
        debouncedSave(reordered);
        return reordered;
      }

      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave]);

  /**
   * Resize a card.
   */
  const resizeCard = useCallback((cardId, newSize) => {
    setCards(prev => {
      const updated = prev.map(c => {
        if (c.id === cardId) return { ...c, size: newSize };
        return c;
      });
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave]);

  /**
   * Reset to defaults.
   */
  const resetDashboard = useCallback(async () => {
    const defaults = await resetToDefaults();
    setCards(defaults);
    return defaults;
  }, []);

  /**
   * Get visible cards sorted by order.
   */
  const getVisibleCards = useCallback(() => {
    return [...cards]
      .filter(c => c.visible)
      .sort((a, b) => a.order - b.order);
  }, [cards]);

  /**
   * Get all cards sorted by order.
   */
  const getAllCards = useCallback(() => {
    return [...cards].sort((a, b) => a.order - b.order);
  }, [cards]);

  const value = {
    cards,
    loading,
    initialized,
    initializePreferences,
    reorderCards,
    toggleCardVisibility,
    resizeCard,
    resetDashboard,
    getVisibleCards,
    getAllCards,
  };

  return (
    <DashboardPreferencesContext.Provider value={value}>
      {children}
    </DashboardPreferencesContext.Provider>
  );
}

export function useDashboardPreferences() {
  const ctx = useContext(DashboardPreferencesContext);
  if (!ctx) {
    throw new Error('useDashboardPreferences must be used within a DashboardPreferencesProvider');
  }
  return ctx;
}
